import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AuditorApiTokenDTO } from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@deepguide-ai/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@deepguide-ai/ui/table'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { ConfirmDialog } from '@/components/confirm-dialog'

/** Auditor self-service and the customer's per-cycle token inventory. */
export function AuditorApiTokensCard({
  auditCycleId,
}: {
  auditCycleId?: string
}) {
  const admin = Boolean(auditCycleId)
  const flag = useFeatureFlag('auditor-pull-api')
  const queryClient = useQueryClient()
  const tokens = useQuery(
    trpc.auditor.listApiTokens.queryOptions(
      auditCycleId ? { auditCycleId } : undefined
    )
  )
  const [showHistory, setShowHistory] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState('')
  const [days, setDays] = useState('90')
  const [allowWrites, setAllowWrites] = useState(false)
  const [revoking, setRevoking] = useState<AuditorApiTokenDTO | null>(null)
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.auditor.listApiTokens.queryKey(),
    })
  const create = useMutation({
    ...trpc.auditor.createApiToken.mutationOptions(),
    gcTime: 0,
  })
  const secret = create.data?.token ?? null
  const revoke = useMutation(trpc.auditor.revokeApiToken.mutationOptions())

  if (!flag.enabled && !tokens.data?.length && !tokens.isError) return null

  const activeTokens = (tokens.data ?? []).filter(
    (token) =>
      !token.revokedAt && new Date(token.expiresAt).getTime() > Date.now()
  )
  const historyCount = (tokens.data?.length ?? 0) - activeTokens.length
  const visibleTokens = showHistory
    ? [
        ...activeTokens,
        ...(tokens.data ?? []).filter((token) => !activeTokens.includes(token)),
      ]
    : activeTokens

  const expiresInDays = Number(days)
  const valid =
    name.trim().length > 0 &&
    Number.isInteger(expiresInDays) &&
    expiresInDays >= 1 &&
    expiresInDays <= 365
  return (
    <Card className={admin ? undefined : 'mt-6'}>
      <CardHeader>
        <div className='flex items-center justify-between gap-4'>
          <CardTitle>
            {admin ? 'Auditor API tokens' : 'Access tokens'}
          </CardTitle>
          {!admin && flag.enabled && (
            <Button size='sm' onClick={() => setCreateOpen(true)}>
              Create token
            </Button>
          )}
        </div>
        <CardDescription>
          {admin
            ? 'Review and revoke tokens owned by auditors assigned to this audit. Only auditors can create tokens. Each token covers their assigned audits in this workspace; revoking it stops access to all of them.'
            : 'Read access to your assigned audits, with optional write access for comments and Control conclusions. Your customer can view and revoke these tokens.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!flag.enabled && (
          <p className='text-muted-foreground mb-3 text-sm'>
            API access is disabled. You can still revoke existing tokens.
          </p>
        )}
        {tokens.isPending ? (
          <p className='text-muted-foreground text-sm'>Loading tokens…</p>
        ) : tokens.isError ? (
          <p role='alert' className='text-destructive text-sm'>
            Could not load tokens.{' '}
            <Button variant='link' onClick={() => tokens.refetch()}>
              Retry
            </Button>
          </p>
        ) : !tokens.data.length ? (
          <p className='text-muted-foreground text-sm'>No API tokens yet.</p>
        ) : (
          <>
            <div className='mb-3 flex items-center justify-between gap-3'>
              <p className='text-sm font-medium'>
                Active tokens ({activeTokens.length})
              </p>
              {historyCount > 0 && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setShowHistory(!showHistory)}
                >
                  {showHistory
                    ? 'Hide history'
                    : `Show history (${historyCount})`}
                </Button>
              )}
            </div>
            {!visibleTokens.length && (
              <p className='text-muted-foreground text-sm'>No active tokens.</p>
            )}
            {visibleTokens.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Token</TableHead>
                    {admin && <TableHead>Auditor</TableHead>}
                    <TableHead>Access</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>
                      <span className='sr-only'>Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleTokens.map((token) => {
                    const expired =
                      new Date(token.expiresAt).getTime() <= Date.now()
                    return (
                      <TableRow key={token.id}>
                        <TableCell>
                          <div className='font-medium'>{token.name}</div>
                          <div className='text-muted-foreground font-mono text-xs'>
                            {token.tokenPrefix}…
                          </div>
                        </TableCell>
                        {admin && (
                          <TableCell>
                            {token.auditorName ?? token.auditorEmail}
                          </TableCell>
                        )}
                        <TableCell>{token.allowWrites ? 'Read/write' : 'Read-only'}</TableCell>
                        <TableCell>
                          {new Date(token.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {token.lastUsedAt
                            ? new Date(token.lastUsedAt).toLocaleString()
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          {new Date(token.expiresAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge
                            status={
                              token.revokedAt || expired ? 'default' : 'green'
                            }
                          >
                            {token.revokedAt
                              ? 'Revoked'
                              : expired
                                ? 'Expired'
                                : 'Active'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {!token.revokedAt && !expired && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => setRevoking(token)}
                            >
                              Revoke
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </CardContent>
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!create.isPending) setCreateOpen(open)
        }}
      >
        <DialogContent className='bg-popover text-popover-foreground'>
          <DialogHeader>
            <DialogTitle>Create auditor API token</DialogTitle>
            <DialogDescription>
              Your token will be shown once. Write access is optional and uses your existing audit permissions.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (!valid || create.isPending) return
              create.mutate(
                { name: name.trim(), expiresInDays, allowWrites },
                {
                  onSuccess: () => {
                    setCreateOpen(false)
                    setName('')
                    setDays('90')
                    setAllowWrites(false)
                    void invalidate()
                  },
                  onError: (error) => toast.error(error.message),
                }
              )
            }}
          >
            <div className='space-y-4 px-6 pb-4'>
              <div className='flex items-start gap-3'>
                <Checkbox id='auditor-token-writes' checked={allowWrites} onCheckedChange={value => setAllowWrites(value === true)} />
                <div className='space-y-1'>
                  <Label htmlFor='auditor-token-writes'>Allow comments and Control conclusions</Label>
                  <p className='text-muted-foreground text-sm'>Enables REST writes in your open audits. Existing tokens remain read-only. Your customer can see and revoke this access.</p>
                </div>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='auditor-token-name'>Name</Label>
                <Input
                  id='auditor-token-name'
                  className='bg-background'
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='Workpaper sync'
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='auditor-token-days'>Expires in days</Label>
                <Input
                  id='auditor-token-days'
                  className='bg-background'
                  type='number'
                  min={1}
                  max={365}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type='submit' disabled={!valid || create.isPending}>
                {create.isPending ? 'Creating…' : 'Create token'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={secret !== null}
        onOpenChange={(open) => {
          if (!open) create.reset()
        }}
      >
        <DialogContent className='bg-popover text-popover-foreground'>
          <DialogHeader>
            <DialogTitle>Save your API token</DialogTitle>
            <DialogDescription>
              Copy this token now. You cannot view it again after closing this
              dialog.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3 px-6 pb-4'>
            <Input
              aria-label='New API token'
              readOnly
              value={secret ?? ''}
              className='bg-background font-mono'
            />
            <Button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(secret!)
                  toast.success('Token copied')
                } catch {
                  toast.error(
                    'Could not copy. Select and copy the token manually.'
                  )
                }
              }}
            >
              Copy token
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => create.reset()}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null)
        }}
        title={`Revoke ${revoking?.name ?? 'token'}?`}
        desc='Scripts using this token will immediately lose access to every audit assigned to this seat. This cannot be undone.'
        destructive
        confirmText='Revoke token'
        isLoading={revoke.isPending}
        handleConfirm={() => {
          if (!revoking) return
          revoke.mutate(
            { id: revoking.id },
            {
              onSuccess: () => {
                setRevoking(null)
                void invalidate()
                toast.success('Token revoked')
              },
              onError: (error) => toast.error(error.message),
            }
          )
        }}
      />
    </Card>
  )
}
